import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Button, Col, List, Row, Tabs, Typography } from 'antd';
import { Avatar, Divider, Spin } from 'antd';
import { isEmpty } from 'lodash';
import { TMetadata } from 'types/metadata';

import { OrderObjectsResp } from '@api/responseModels/orderObjects/orderObjectsResponse';
import { Loader } from '@atoms/loader';
import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import RenderIf from '@common/RenderIf';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActivityCategoryList } from '@molecules/ActivityCategoryList';
import { TopActivityCategories, useTopActivityCategories } from '@molecules/topActivityCategories';
import { ObjectProfile } from '@pages/objects/contents/profile';
import { apiRequest } from '@utils/apiRequest';
import { imagePicker } from '@utils/imagePicker';

const OBJECT_LIST_ID = 'create-order-object-list';

interface Props {
    setOrderObjectId: React.Dispatch<React.SetStateAction<number>>;
    orderObjectId: number;
    customerId: number;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    setIsObjectCreationTab: React.Dispatch<React.SetStateAction<boolean>>;
    ref?: React.Ref<{ doCreate: () => Promise<void> }>;
}
export const OrderObjectStep = ({
    setOrderObjectId,
    orderObjectId,
    customerId,
    setCurrentStep,
    setIsObjectCreationTab,
    ref,
}: Props) => {
    const [activeKey, setActiveKey] = useState('1');
    const [isObjectListTabDisabled, setIsObjectListTabDisabled] = useState(false);

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [objectList, setObjectList] = useState<Array<any>>([]);
    const [currentMeta, setCurrentMeta] = useState<TMetadata>(null);

    const [metadata, setMetadata] = useState(null);
    const [categories, setCategories] = useState([]);
    const [activityCategories, setActivityCategories] = useState([]);
    const topCategories = useTopActivityCategories('object');
    const [isLoading, setIsLoading] = useState(false);

    const fetchCategories = async (page = 1) => {
        setIsLoading(true);

        try {
            const response = await apiRequest(async (api) => await api.getCRMCategories(page));
            const newData = response.data.data;

            if (page === 1) {
                setCategories(newData);
            } else {
                const updatedCategories = [...categories, ...newData];
                setCategories(updatedCategories);
            }

            setMetadata(response.data.meta);
        } catch {}

        setIsLoading(false);
    };

    /**
     * Выбирает только галочка, клик по карточке ничего не делает. Она же ведёт
     * на следующий шаг; кнопка «Далее» внизу остаётся для возврата «Назад»,
     * когда объект уже выбран.
     */
    const handleSelectButtonClick = (e, object) => {
        e.stopPropagation();
        setOrderObjectId(object.id);
        setCurrentStep(2);
    };

    async function getObjectList(page: number = currentPage) {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const resp = await CRMAPIManager.request<OrderObjectsResp>(async (api) => {
                // включая черновики объектов
                return await api.getOrderObjects(null, customerId, page, {
                    is_draft: 'all',
                });
            });
            if (resp.data.data.length === 0) {
                setActiveKey('2');
                setIsObjectCreationTab(true);
                setIsObjectListTabDisabled(true);
                messageService.sendInfo('Нет объектов, создайте новый');
            } else {
                setIsObjectListTabDisabled(false);
            }

            setObjectList((wl) => [...wl, ...resp.data.data]);
            setCurrentMeta(resp.data.meta);
        } catch (err) {
            messageService.sendError(err.message);
        }
        setIsLoading(false);
    }
    const handleChangePage = () => {
        getObjectList(currentPage + 1).then(() => {
            setCurrentPage((prev) => prev + 1);
        });
    };

    function beforeMountObjectList() {
        if (isEmpty(objectList)) {
            setCurrentPage(1);
            getObjectList(1);
        }
    }

    useEffect(() => {
        beforeMountObjectList();
    }, []);

    const [objectName, setObjectName] = useState('');
    const [selectedValues, setSelectedValues] = useState({});
    const [valueLabels, setValueLabels] = useState({});
    const itemsRef = useRef([]);

    const setItemsRef = (ref) => {
        itemsRef.current = ref;
    };

    const doCreate = async () => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        try {
            const response = await apiRequest((api) =>
                api.createOrderObject({
                    name: objectName,
                    order_object_category_id: +activityCategories?.[0]?.id,
                    ownerable_type: `App\\Models\\Api\\V1\\CustomerProfile`,
                    ownerable_id: customerId,
                    fields: itemsRef.current
                        .filter((item) => selectedValues[item.id] !== undefined)
                        .map((item) => {
                            const selectedValueId = selectedValues[item.id];
                            const valueLabel = valueLabels[selectedValueId];

                            // бэк ждёт value объектом { id?, value }; у кастомных
                            // значений временный id (> 1e12) не отправляем
                            return {
                                name: item.name,
                                value: {
                                    id:
                                        selectedValueId > 1000000000000
                                            ? undefined
                                            : selectedValueId,
                                    value: String(valueLabel || selectedValueId),
                                },
                            };
                        }),
                })
            );

            setOrderObjectId(response.data.data.id);
            setActivityCategories([]);

            setCurrentPage(1);
            setObjectList([]);
            await getObjectList(1);
            setCurrentStep(2);
        } catch (error) {
            console.error('Error in doCreate:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        doCreate,
    }));

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        setIsObjectCreationTab(activeKey === '2');
    }, [activeKey]);

    return (
        <>
            <Tabs
                activeKey={activeKey}
                defaultActiveKey="1"
                onChange={(key) => {
                    setActiveKey(key);
                }}
                centered
                items={[
                    {
                        key: '1',
                        label: 'Объекты',
                        disabled: isObjectListTabDisabled,
                        children: (
                            // Прокручивает список сам контейнер: высоты в vh
                            // у InfiniteScroll больше нет
                            <div className="picker-modal__list" id={OBJECT_LIST_ID}>
                            <InfiniteScroll
                                scrollableTarget={OBJECT_LIST_ID}
                                dataLength={objectList.length}
                                next={handleChangePage}
                                hasMore={objectList.length < currentMeta?.total}
                                loader={
                                    <Divider plain>
                                        <Spin />
                                    </Divider>
                                }
                                endMessage={<Divider plain>Все объекты загружены :^)</Divider>}
                            >
                                <>
                                    {isLoading && <Loader />}
                                    <List
                                        className="customer-card-list"
                                        dataSource={objectList}
                                        itemLayout="horizontal"
                                        renderItem={(item) => (
                                            <List.Item className="customer-card" key={item.id}>
                                                <Row style={{ width: '100%' }}>
                                                    <Col span={3}>
                                                        <Avatar
                                                            className="avatar"
                                                            src={imagePicker({
                                                                picture: item?.picture,
                                                            })}
                                                        />
                                                    </Col>
                                                    <Col span={18}>
                                                        <Row>
                                                            <Col>
                                                                <h3 className="title">
                                                                    {item.name}
                                                                </h3>
                                                                {!!item.is_draft && (
                                                                    <Typography.Text type="danger">
                                                                        Черновик
                                                                    </Typography.Text>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                    <Col span={3} className="button-select">
                                                        <Button
                                                            icon={
                                                                <FontAwesomeIcon icon={faCheck} />
                                                            }
                                                            shape="circle"
                                                            type={
                                                                item?.id === orderObjectId
                                                                    ? 'primary'
                                                                    : 'default'
                                                            }
                                                            onClick={(e) =>
                                                                handleSelectButtonClick(e, item)
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            </List.Item>
                                        )}
                                    />
                                </>
                            </InfiniteScroll>
                            </div>
                        ),
                    },

                    {
                        key: '2',
                        label: 'Новый объект',
                        children: (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                    position: 'relative',
                                }}
                            >
                                <RenderIf condition={!activityCategories?.[0]?.id}>
                                    <h3 style={{ textAlign: 'center' }}>Выбор категории</h3>
                                    <TopActivityCategories
                                        categories={topCategories}
                                        selected={activityCategories}
                                        onSelect={(category) => setActivityCategories([category])}
                                    />
                                    <ActivityCategoryList
                                        categories={categories}
                                        activityCategories={activityCategories}
                                        setActivityCategories={setActivityCategories}
                                        metadata={metadata}
                                        multiselect={false}
                                        onLoadMore={() => {
                                            if (
                                                metadata &&
                                                metadata.current_page < metadata.last_page
                                            ) {
                                                return fetchCategories(metadata.current_page + 1);
                                            }
                                            return Promise.resolve();
                                        }}
                                    />
                                </RenderIf>
                                {/* ToDo: Описать типы для компонента */}
                                <RenderIf condition={!isEmpty(activityCategories)}>
                                    <ObjectProfile
                                        key={activityCategories?.[0]?.id}
                                        objectId={activityCategories?.[0]?.id}
                                        setExternalSelectedValues={setSelectedValues}
                                        setExternalValueLabels={setValueLabels}
                                        setExternalItemsRef={setItemsRef}
                                        setExternalObjectName={setObjectName}
                                        mode="create"
                                        withHeader={false}
                                    />
                                </RenderIf>
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
};
